import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input, Label } from "./ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Sparkles, Plus, Link2, FileDown, MapPin, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export const validateCoords = (lat, lng) => {
  if (lat === "" && lng === "") return { ok: true, empty: true };
  if (lat === "" || lng === "") {
    const has = lat === "" ? "longitude" : "latitude";
    const missing = lat === "" ? "latitude (north–south position, e.g. 1.35 for Singapore)" : "longitude (east–west position, e.g. 103.82 for Singapore)";
    return { ok: false, field: lat === "" ? "lat" : "lng", msg: `Both latitude and longitude are needed — you've entered only ${has}. Add the ${missing}.` };
  }
  const la = Number(String(lat).replace(",", ".")), ln = Number(String(lng).replace(",", "."));
  if (String(lat).includes(",") || String(lng).includes(",") || Number.isNaN(la) || Number.isNaN(ln)) {
    return { ok: false, field: Number.isNaN(la) || String(lat).includes(",") ? "lat" : "lng", msg: "Coordinates must be numbers using a dot as the decimal separator, e.g. 1.3521." };
  }
  if (la < -90 || la > 90) return { ok: false, field: "lat", msg: `Latitude must be between −90 and 90 (you entered ${lat}). Tip: Singapore is around 1.35, 103.82 — you may have swapped latitude and longitude.` };
  if (ln < -180 || ln > 180) return { ok: false, field: "lng", msg: `Longitude must be between −180 and 180 (you entered ${lng}). Tip: Singapore is around 1.35, 103.82 — you may have swapped latitude and longitude.` };
  return { ok: true, lat: la, lng: ln };
};

export const MapLink = ({ lat, lng, testid }) => {
  const v = validateCoords(String(lat ?? ""), String(lng ?? ""));
  const valid = v.ok && !v.empty;
  return (
    <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={!valid}
      title={valid ? "Open this point in Google Maps (new tab)" : "Enter valid coordinates first"}
      onClick={() => valid && window.open(`https://www.google.com/maps?q=${v.lat},${v.lng}`, "_blank", "noopener")}
      data-testid={testid}>
      <MapPin className="h-3 w-3" /> View on map
    </Button>
  );
};

const SELECTS = {
  site_type: ["office", "nursery", "warehouse", "plant", "depot", "retail", "other"],
  elevation_category: ["low-lying", "elevated"],
  ownership: ["owned", "leased"],
  operating_pattern: ["day", "24-7", "seasonal"],
  water_source: ["mains", "rainwater", "mixed"],
};
const BOOLS = ["backup_power", "flood_protection", "insurance_coverage", "bcp"];
const NUMS = ["floor_area_m2", "building_age_years", "criticality", "headcount", "outdoor_work_pct",
  "resilience_rating", "asset_value_sgd", "annual_output_sgd"];
const LABELS = {
  site_type: "Site type", floor_area_m2: "Floor area (m²)", elevation_category: "Elevation",
  building_age_years: "Building age (yrs)", ownership: "Ownership", criticality: "Criticality (1-5)",
  headcount: "Headcount", operating_pattern: "Operating pattern", outdoor_work_pct: "% outdoor work",
  resilience_rating: "Resilience (1-5)", asset_value_sgd: "Asset value (S$)", annual_output_sgd: "Annual output (S$)",
  backup_power: "Backup power", flood_protection: "Flood protection", water_source: "Water source",
  insurance_coverage: "Insurance coverage", bcp: "Business continuity plan",
};

export function Facility360({ facility, canEdit, onClose, onSaved }) {
  const [attrs, setAttrs] = useState({});
  const [coords, setCoords] = useState({ lat: "", lng: "" });
  const [insight, setInsight] = useState(null);
  const [history, setHistory] = useState([]);
  const [proposals, setProposals] = useState(null);
  const [busy, setBusy] = useState(false);
  const [composeHz, setComposeHz] = useState([]);
  const [coordErr, setCoordErr] = useState(null);
  const [tab, setTab] = useState("profile");

  const load = () => {
    api.get(`/hazards/facility/${facility.id}/insight`).then((r) => setInsight(r.data)).catch(() => setInsight({ hazards: [], register_risks: [], scenarios: [] }));
    api.get(`/facilities/${facility.id}/history`).then((r) => setHistory(r.data.history)).catch(() => {});
  };
  useEffect(() => {
    setAttrs(Object.fromEntries(Object.keys(LABELS).map((k) => [k, facility[k] ?? ""])));
    setCoords({ lat: facility.lat ?? "", lng: facility.lng ?? "" });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facility.id]);

  const save = async () => {
    const clean = {};
    Object.entries(attrs).forEach(([k, v]) => {
      if (v === "" || v == null) return;
      clean[k] = NUMS.includes(k) ? +v : BOOLS.includes(k) ? v === true || v === "true" : v;
    });
    const body = { name: facility.name, location: facility.location, subsidiary: facility.subsidiary, attributes: clean };
    const v = validateCoords(String(coords.lat), String(coords.lng));
    if (!v.ok) { setCoordErr(v); toast.error("Fix the coordinate fields before saving"); return; }
    setCoordErr(null);
    if (!v.empty) { body.lat = v.lat; body.lng = v.lng; }
    try {
      const r = await api.patch(`/facilities/${facility.id}`, body);
      toast.success("Facility profile saved");
      if (!v.empty && !r.data.placed) {
        toast.info("Saved — but this point is more than 100 km from any curated hazard location, so hazard levels can't be assigned yet. Check the values, or ask your platform admin to add a curated location for this area.", { duration: 12000 });
      }
      onSaved(); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const compose = async () => {
    try {
      const { data } = await api.post("/hazards/compose", {
        facility_id: facility.id, hazard_codes: composeHz,
        title: `${composeHz.join(" + ")} composite — ${facility.name}`,
      });
      toast.success(`Composed risk created (${data.band}, ${data.score})`);
      setComposeHz([]); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const generate = async (riskId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/hazards/risks/${riskId}/scenarios/generate`);
      setProposals({ riskId, list: data.proposals });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const saveScen = async () => {
    try {
      await api.post(`/hazards/risks/${proposals.riskId}/scenarios/save`, { scenarios: proposals.list });
      toast.success("Scenarios saved and linked to the risk");
      setProposals(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const mkInitiative = async (sid, idx) => {
    try {
      await api.post(`/hazards/decision-scenarios/${sid}/mitigations/initiative`, { mitigation_index: idx });
      toast.success("Initiative created and linked"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const brief = async () => {
    try {
      const r = await api.get(`/hazards/facility/${facility.id}/board-brief`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${facility.name.replace(/[^a-z0-9]+/gi, "-")}-board-brief.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Board brief downloaded");
    } catch { toast.error("Could not generate the board brief"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="facility-360-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center justify-between gap-3 pr-6">
            <span>{facility.name} — 360°</span>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={brief} data-testid="f360-board-brief-btn">
              <FileDown className="h-3.5 w-3.5" /> Board brief (PDF)
            </Button>
          </DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="profile" data-testid="f360-tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="hazards" data-testid="f360-tab-hazards">Hazards &amp; Risks</TabsTrigger>
            <TabsTrigger value="history" data-testid="f360-tab-history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-1">
              <div>
                <Label className="text-[11px]">Latitude</Label>
                <Input type="text" inputMode="decimal" className={coordErr?.field === "lat" ? "border-red-500" : ""} value={coords.lat} onChange={(e) => { setCoords({ ...coords, lat: e.target.value }); setCoordErr(null); }} disabled={!canEdit} data-testid="f360-lat" />
              </div>
              <div>
                <Label className="text-[11px]">Longitude</Label>
                <Input type="text" inputMode="decimal" className={coordErr?.field === "lng" ? "border-red-500" : ""} value={coords.lng} onChange={(e) => { setCoords({ ...coords, lng: e.target.value }); setCoordErr(null); }} disabled={!canEdit} data-testid="f360-lng" />
              </div>
              <div className="flex items-end pb-1 gap-2">
                <MapLink lat={coords.lat} lng={coords.lng} testid="f360-map-btn" />
                <span className="text-[10px] text-muted-foreground">Saving re-matches the nearest curated hazard location (≤100 km), updating placement immediately.</span>
              </div>
            </div>
            {coordErr && <p className="text-[11px] text-red-400 mb-2" data-testid="f360-coord-error">{coordErr.msg}</p>}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.keys(LABELS).map((k) => (
                <div key={k}>
                  <Label className="text-[11px]">{LABELS[k]}</Label>
                  {SELECTS[k] ? (
                    <select className="w-full h-9 bg-surface border border-border rounded-sm text-sm px-2" value={attrs[k] ?? ""}
                      onChange={(e) => setAttrs({ ...attrs, [k]: e.target.value })} disabled={!canEdit} data-testid={`f360-${k}`}>
                      <option value="">—</option>
                      {SELECTS[k].map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : BOOLS.includes(k) ? (
                    <select className="w-full h-9 bg-surface border border-border rounded-sm text-sm px-2" value={String(attrs[k] ?? "")}
                      onChange={(e) => setAttrs({ ...attrs, [k]: e.target.value === "" ? "" : e.target.value === "true" })} disabled={!canEdit} data-testid={`f360-${k}`}>
                      <option value="">—</option><option value="true">yes</option><option value="false">no</option>
                    </select>
                  ) : (
                    <Input type="number" value={attrs[k] ?? ""} onChange={(e) => setAttrs({ ...attrs, [k]: e.target.value })} disabled={!canEdit} data-testid={`f360-${k}`} />
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">Protective attributes (backup power, flood protection, BCP, insurance) visibly reduce the susceptibility score in the derivation.</p>
            {canEdit && <Button variant="primary" className="mt-3" onClick={save} data-testid="f360-save-btn">Save profile</Button>}
          </TabsContent>

          <TabsContent value="hazards" className="mt-3 space-y-3">
            {insight && !insight.placeable && (
              <div className="border border-[#FFB800]/50 bg-[#FFB800]/10 rounded-sm p-3 text-xs space-y-2" data-testid="f360-not-placeable">
                {insight.facility?.lat == null ? (
                  <>
                    <p>This facility has <b>no coordinates yet</b>, so hazard levels can't be assessed. Open the Profile tab, enter its latitude/longitude (or pick a curated location), and its hazard exposure will appear here immediately.</p>
                    <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => setTab("profile")} data-testid="f360-goto-profile-btn">Go to Profile</Button>
                  </>
                ) : (
                  <>
                    <p>Coordinates are set ({insight.facility.lat}, {insight.facility.lng}), but there's <b>no curated hazard location within 100 km</b>, so no reference hazard levels apply. Verify the coordinates on the map, or request coverage from the platform operator.</p>
                    <div className="flex gap-2 flex-wrap">
                      <MapLink lat={insight.facility.lat} lng={insight.facility.lng} testid="f360-np-map-btn" />
                      <Button size="sm" variant="outline" className="h-6 text-[10px]" data-testid="f360-request-coverage-btn"
                        onClick={async () => {
                          try {
                            const r = await api.post("/hazards/coverage-requests", { name: facility.name, lat: insight.facility.lat, lng: insight.facility.lng, facility_id: facility.id });
                            toast.success(r.data.message);
                          } catch (e) { toast.error(apiErr(e)); }
                        }}>Request coverage for this area</Button>
                    </div>
                  </>
                )}
              </div>
            )}
            {(insight?.hazards || []).map((h, i) => <HazardExplain key={i} h={h} insight={insight} />)}
            {canEdit && insight?.placeable && (
              <div className="card-flat p-3">
                <div className="label-eyebrow mb-1">Compose a multi-hazard risk</div>
                <div className="flex flex-wrap items-center gap-2">
                  {["HZ-1", "HZ-2", "HZ-3", "HZ-4"].map((c) => (
                    <label key={c} className="text-xs flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={composeHz.includes(c)} data-testid={`f360-compose-${c}`}
                        onChange={(e) => setComposeHz(e.target.checked ? [...composeHz, c] : composeHz.filter((x) => x !== c))} /> {c}
                    </label>
                  ))}
                  <Button size="sm" variant="outline" disabled={!composeHz.length} onClick={compose} data-testid="f360-compose-btn"><Plus className="h-3 w-3" /> Compose risk</Button>
                </div>
              </div>
            )}
            <div className="label-eyebrow">Register risks at this facility</div>
            {(insight?.register_risks || []).map((r) => (
              <div key={r.id} className="border border-border rounded-sm p-2.5 text-xs" data-testid={`f360-risk-${r.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-medium">{r.title}</span>
                  <span className="flex gap-1 items-center">
                    {r.hazard_codes.map((c) => <Badge key={c} variant="outline" className="text-[9px] font-mono">{c}</Badge>)}
                    <Badge variant="lime" className="text-[9px]">{r.lane}</Badge>
                    {canEdit && <Button size="sm" variant="ghost" className="h-6 text-[10px]" disabled={busy} onClick={() => generate(r.id)} data-testid={`f360-gen-${r.id}`}><Sparkles className="h-3 w-3" /> {busy ? "…" : "Generate decision scenarios"}</Button>}
                  </span>
                </div>
              </div>
            ))}
            <div className="label-eyebrow">Decision scenarios (linked to risks here)</div>
            {(insight?.scenarios || []).length === 0 && <div className="text-xs text-muted-foreground">None yet — generate from a register risk above.</div>}
            {(insight?.scenarios || []).map((s) => <ScenarioCard key={s.id} sid={s.id} onInitiative={mkInitiative} />)}
          </TabsContent>

          <TabsContent value="history" className="mt-3 space-y-2">
            {history.length === 0 && <div className="text-xs text-muted-foreground">No edits recorded yet.</div>}
            {history.map((h) => (
              <div key={h.id} className="border border-border rounded-sm p-2.5 text-[11px]" data-testid={`f360-history-${h.id}`}>
                <div className="font-mono text-muted-foreground">{h.at?.slice(0, 16).replace("T", " ")} · {h.by}</div>
                {Object.entries(h.changes).map(([k, c]) => (
                  <div key={k}>{LABELS[k] || k}: <span className="text-muted-foreground">{String(c.from ?? "—")}</span> → <span className="text-accent">{String(c.to)}</span></div>
                ))}
              </div>
            ))}
          </TabsContent>
        </Tabs>

        <Dialog open={!!proposals} onOpenChange={(o) => !o && setProposals(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="f360-proposals-dialog">
            <DialogHeader><DialogTitle>AI scenario proposals — review before saving</DialogTitle></DialogHeader>
            {(proposals?.list || []).map((s, i) => (
              <div key={i} className="border border-border rounded-sm p-3 text-xs space-y-1">
                <div className="font-semibold flex justify-between">{s.name}
                  <span className="font-mono text-accent">S${(s.impact_low_sgd || 0).toLocaleString()}–{(s.impact_high_sgd || 0).toLocaleString()}</span></div>
                <p className="text-muted-foreground">{s.narrative}</p>
                <div className="text-[10px]">Assumptions: {(s.assumptions || []).join("; ")}</div>
                <div className="text-[10px]">Mitigations: {(s.mitigations || []).join(" · ")}</div>
              </div>
            ))}
            <Button variant="primary" onClick={saveScen} data-testid="f360-save-scenarios-btn">Save scenarios to the Scenarios area</Button>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

function ScenarioCard({ sid, onInitiative }) {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [inits, setInits] = useState({});
  useEffect(() => {
    api.get("/hazards/decision-scenarios").then((r) => {
      setS(r.data.scenarios.find((x) => x.id === sid));
      setInits(r.data.initiatives || {});
    }).catch(() => {});
  }, [sid]);
  if (!s) return null;
  return (
    <div className="border border-border rounded-sm p-2.5 text-xs space-y-1" data-testid={`f360-scenario-${s.id}`}>
      <div className="flex justify-between font-medium">{s.name}
        <span className="font-mono text-accent">S${(s.impact_range?.low_sgd || 0).toLocaleString()}–{(s.impact_range?.high_sgd || 0).toLocaleString()}</span></div>
      <p className="text-muted-foreground">{s.narrative}</p>
      <div className="text-[10px] text-muted-foreground">Source risk: {s.risk_title}</div>
      {(s.mitigations || []).map((m, i) => {
        const init = m.initiative_id ? inits[m.initiative_id] : null;
        return (
          <div key={i} className="flex items-center justify-between gap-2">
            <span>• {m.text}</span>
            {m.initiative_id
              ? (
                <Badge variant="success" className="text-[9px] cursor-pointer hover:opacity-80" onClick={() => navigate("/initiatives")}
                  data-testid={`f360-init-badge-${s.id}-${i}`} title="Open the Investment Register">
                  <Link2 className="h-2.5 w-2.5 mr-0.5" />
                  {init ? `${init.status.replace(/_/g, " ")} · ${init.progress_percent}%` : "initiative"}
                </Badge>
              )
              : <Button size="sm" variant="ghost" className="h-5 text-[9px]" onClick={() => onInitiative(s.id, i)} data-testid={`f360-mk-init-${s.id}-${i}`}>Create initiative</Button>}
          </div>
        );
      })}
    </div>
  );
}


function HazardExplain({ h, insight }) {
  const [open, setOpen] = useState(false);
  const loc = insight?.location;
  const fac = insight?.facility;
  return (
    <div className="border border-border rounded-sm text-xs" data-testid={`f360-hazard-${h.hazard_code}`}>
      <div className="p-2.5 flex items-center justify-between gap-2">
        <span><span className="font-mono text-muted-foreground">{h.hazard_code}</span> {h.hazard_name} — level {h.hazard_level}/5</span>
        <span className="flex items-center gap-2">
          <Badge variant={h.band === "High" || h.band === "Critical" ? "danger" : h.band.startsWith("Medium") ? "warning" : "outline"} className="text-[9px]">{h.band} · {h.score}</Badge>
          {h.dollar_impact_sgd ? <span className="font-mono">S${h.dollar_impact_sgd.toLocaleString()}</span> : null}
          <Button size="sm" variant="ghost" className="h-5 text-[9px]" onClick={() => setOpen(!open)} data-testid={`f360-explain-${h.hazard_code}`}>
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} How was this calculated?
          </Button>
        </span>
      </div>
      {open && (
        <div className="border-t border-border p-2.5 space-y-2 bg-surface" data-testid={`f360-explain-panel-${h.hazard_code}`}>
          <div>
            <div className="label-eyebrow text-[9px]">1 · Location matching</div>
            <p>Facility coordinates ({fac?.lat ?? "—"}, {fac?.lng ?? "—"}) matched curated location <b>{loc?.name || h.location_name}</b>{loc?.distance_km != null ? ` (${loc.distance_km} km away)` : ""} — rule: nearest curated location within 100 km.</p>
          </div>
          <div>
            <div className="label-eyebrow text-[9px]">2 · Hazard level source</div>
            <p>Shared platform Hazard Catalog entry <b>{h.hazard_code} {h.hazard_name}</b>; curated level <b>{h.hazard_level}/5</b> maintained for {loc?.name || h.location_name} — reference data, identical for every tenant at this location.</p>
          </div>
          <div>
            <div className="label-eyebrow text-[9px]">3 · Score calculation</div>
            {(h.derivation?.inputs || []).map((i, k) => (
              <div key={k} className="flex justify-between gap-3 font-mono text-[11px]">
                <span className="text-muted-foreground">{i.input} ({i.direction}{i.weight != null ? `, w=${i.weight}` : ""})</span>
                <span>{i.value} → {i.contribution >= 0 ? "+" : ""}{i.contribution}</span>
              </div>
            ))}
            <div className="flex justify-between font-mono text-[11px] border-t border-border mt-1 pt-1"><span>Final score</span><b>{h.score}</b></div>
          </div>
          <div>
            <div className="label-eyebrow text-[9px]">4 · Band &amp; dollar impact</div>
            <p>Band thresholds: &lt;40 Low · 40–55 Medium · 55–70 Medium-High · ≥70 High. {h.derivation?.impact_formula ? `Impact = ${h.derivation.impact_formula}` : ""}{h.dollar_impact_sgd ? ` → S$${h.dollar_impact_sgd.toLocaleString()} using this facility's asset value.` : ""}</p>
          </div>
          <div>
            <div className="label-eyebrow text-[9px]">5 · Source data</div>
            <p>Inputs live in: this facility's Profile tab (resilience, criticality, protections, asset value) · shared Hazard Catalog &amp; curated location levels (Hazards &amp; Locations) · {h.derivation?.method || "default scoring method (adjustable in Scoring settings)"}. Change any input and this score recomputes live.</p>
          </div>
        </div>
      )}
    </div>
  );
}
